import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const SERVICES = [
  {
    title: "App Development",
    subtitle: "Mobile & Desktop Utilities",
    desc: "We build high-performance utility applications — system cleaners, optimizers, file managers, and AI-powered tools — for iOS, Android, macOS, and Windows. Our team handles the entire cycle: architecture, UI/UX design, development, QA, and release.",
    icon: "ri-smartphone-line",
    features: [
      "iOS & Android native development",
      "macOS & Windows desktop apps",
      "AI/ML integration into utilities",
      "Cross-platform solutions",
      "App Store & Play Store submission",
      "Post-launch maintenance & updates",
    ],
    image: "https://readdy.ai/api/search-image?query=Close-up%20of%20hands%20typing%20on%20laptop%20keyboard%20with%20code%20on%20screen%20and%20smartphone%20beside%20it%2C%20warm%20wooden%20desk%20surface%2C%20natural%20daylight%20from%20window%2C%20warm%20amber%20and%20cream%20tones%2C%20editorial%20lifestyle%20photography%20style%2C%20real%20workspace%20scene%2C%20high%20detail%2C%20authentic%20candid%20composition&width=800&height=600&seq=service-dev-intellivex&orientation=landscape",
  },
  {
    title: "IT Consulting",
    subtitle: "Technology Strategy & Advisory",
    desc: "We help teams make informed technology decisions, optimize development processes, and implement modern tools. From architecture reviews to technology stack selection — we guide you toward scalable and maintainable solutions.",
    icon: "ri-brain-line",
    features: [
      "Architecture & tech stack audits",
      "Process optimization & automation",
      "Team scaling strategies",
      "Security & compliance guidance",
      "Cloud infrastructure planning",
      "DevOps workflow implementation",
    ],
    image: "https://static.readdy.ai/image/b83d215482d53eb69b064b5c4f8c0569/39c4d214a84cbf17065956fde96a9a3b.png",
  },
  {
    title: "Digital Marketing",
    subtitle: "App Promotion & User Acquisition",
    desc: "We design and execute user acquisition strategies, strengthen app store presence, boost brand awareness, and help applications find their audience among millions of competitors. ASO, media buying, and scaling — all under one roof.",
    icon: "ri-megaphone-line",
    features: [
      "App Store Optimization (ASO)",
      "Media buying & paid UA campaigns",
      "Creative production & A/B testing",
      "Analytics & retention optimization",
      "Influencer & partnership marketing",
      "Brand positioning & messaging",
    ],
    image: "https://readdy.ai/api/search-image?query=Creative%20marketing%20studio%20workspace%20with%20multiple%20monitors%20showing%20charts%20and%20ad%20creatives%2C%20colorful%20mood%20board%20on%20wall%2C%20warm%20terracotta%20and%20amber%20tones%2C%20editorial%20lifestyle%20photography%20style%2C%20real%20creative%20team%20environment%2C%20high%20detail%2C%20authentic%20professional%20composition&width=800&height=600&seq=service-marketing-intellivex&orientation=landscape",
  },
];

const PROCESS = [
  {
    step: "01",
    title: "Discovery & Strategy",
    desc: "We analyze your goals, target audience, and competitive landscape to build a clear roadmap for success.",
  },
  {
    step: "02",
    title: "Design & Development",
    desc: "Our cross-functional team designs intuitive interfaces and writes clean, scalable code that performs.",
  },
  {
    step: "03",
    title: "Launch & ASO",
    desc: "We handle store submission, metadata optimization, and initial marketing push for maximum visibility.",
  },
  {
    step: "04",
    title: "Scale & Optimize",
    desc: "Through data-driven UA campaigns and continuous product improvements, we grow your user base sustainably.",
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-cream-50 font-sans text-ink-950">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Busy%20modern%20tech%20office%20with%20developers%20working%20at%20monitors%20and%20laptops%2C%20warm%20natural%20lighting%20from%20large%20windows%2C%20exposed%20brick%20and%20wooden%20elements%2C%20real%20candid%20workplace%20scene%20with%20people%20in%20focus%2C%20warm%20amber%20and%20cream%20interior%20tones%2C%20editorial%20documentary%20photography%20style%2C%20high%20detail%2C%20authentic%20professional%20composition&width=1600&height=900&seq=services-hero-01&orientation=landscape"
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink-950/70 via-ink-950/50 to-ink-950/30"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-amber-200">
                  Our Services
                </span>
              </div>
              <h1 className="font-display font-bold text-cream-50 leading-[1.08] tracking-tight text-4xl md:text-5xl lg:text-6xl">
                Full-cycle development and promotion
              </h1>
              <p className="mt-5 text-sm md:text-base text-cream-50/80 leading-relaxed max-w-lg">
                Three interconnected directions that cover the entire lifecycle of a utility application — from the first line of code to millions of users.
              </p>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="relative w-full bg-cream-50 py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="space-y-16 md:space-y-20">
              {SERVICES.map((svc, i) => {
                const isReversed = i % 2 === 1;
                return (
                  <div key={svc.title} className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
                    <div className={`lg:col-span-2 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                      <div className="relative rounded-2xl overflow-hidden border border-ink-950/10">
                        <div className="relative aspect-[4/3] w-full">
                          <img
                            src={svc.image}
                            alt={svc.title}
                            className="absolute inset-0 w-full h-full object-cover object-center"
                          />
                        </div>
                      </div>
                    </div>
                    <div className={`lg:col-span-3 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100">
                          <i className={`${svc.icon} text-xl text-amber-600`}></i>
                        </span>
                        <div>
                          <h3 className="font-display font-bold text-2xl md:text-3xl text-ink-950">{svc.title}</h3>
                          <p className="text-xs uppercase tracking-widest text-ink-500 font-medium mt-0.5">{svc.subtitle}</p>
                        </div>
                      </div>
                      <p className="text-sm md:text-base text-ink-700 leading-relaxed mt-4">
                        {svc.desc}
                      </p>
                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {svc.features.map((f) => (
                          <div key={f} className="flex items-center gap-2.5 text-sm text-ink-700">
                            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-moss-100 shrink-0">
                              <i className="ri-check-line text-[10px] text-moss-600"></i>
                            </span>
                            {f}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="relative w-full bg-cream-100 py-20 md:py-28 border-y border-ink-950/5">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-moss-500"></span>
                <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-ink-700">
                  How We Work
                </span>
              </div>
              <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl">
                From idea to scale in four steps
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {PROCESS.map((p) => (
                <div key={p.step} className="group bg-cream-50 rounded-2xl border border-ink-950/5 p-7 md:p-8 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                  <span className="inline-block font-display font-bold text-3xl text-amber-400 mb-4">{p.step}</span>
                  <h3 className="font-display font-bold text-lg text-ink-950 mb-2">{p.title}</h3>
                  <p className="text-sm text-ink-700 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full bg-cream-50 py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14 text-center">
            <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Not sure which service fits?
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-sm md:text-base text-ink-700 leading-relaxed">
              Tell us about your project and we&rsquo;ll recommend the right mix of development, consulting, and marketing.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-ink-950 px-7 py-3.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Get in touch
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-sm font-medium text-ink-700 hover:text-ink-950 transition-colors cursor-pointer whitespace-nowrap border border-ink-950/10 hover:border-ink-950/20 px-7 py-3.5 rounded-full"
              >
                Explore our products
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}